import React from 'react';
import {Board} from './black_hole_game';
import type {Hint} from './useSolverHints';
import './BlackHole.css';
import BoardView from './BoardView';
import {useEndingProgress} from './useGamePresentation';

interface BlackHoleGameProps {
    board: Board;
    setBoard: React.Dispatch<React.SetStateAction<Board>>;
    playerNames: [string, string];
    showUndoButton?: boolean;
    /** The human's player number (1 or 2), or undefined when both sides are human. */
    humanPlayer?: 1 | 2;
    hints?: Map<string, Hint>;
    /** A live hint search is in progress; show placeholders on unresolved cells. */
    hintsPending?: boolean;
    /** Which numeric overlay to show on empty cells. */
    numericOverlay?: 'off' | 'margins' | 'cellpoints' | 'stepcost';
    canPlay?: boolean;
    suggestedCell?: string;
}

const BlackHoleGame: React.FC<BlackHoleGameProps> = ({board, setBoard, playerNames, showUndoButton, humanPlayer, hints, hintsPending, numericOverlay, canPlay = true, suggestedCell}) => {
    const gameOver = board.isGameOver();
    const endingProgress = useEndingProgress(board);
    const resultReady = endingProgress === 1;

    const handleCellClick = (row: number, col: number) => {
        if (!canPlay || gameOver || board.at(row, col) !== 0) {
            return;
        }

        try {
            setBoard(board.play(row, col));
        } catch (error) {
            console.error("Error making a move:", error);
            // Optionally, display an error to the user
        }
    };

    const handleRestart = () => {
        setBoard(new Board(board.rows));
    };

    const handleUndo = () => {
        if (board.filled === 0) {
            return;
        }
        // Always retract the latest ply. When there's an AI opponent, keep
        // retracting until it's the human's turn again, so undo never leaves the
        // AI to move (which would make it immediately replay).
        let target = board.undoMove();
        if (humanPlayer !== undefined) {
            while (target.filled > 0 && (target.nextMove() > 0 ? 1 : 2) !== humanPlayer) {
                target = target.undoMove();
            }
        }
        setBoard(target);
    }

    // Calculate kept points directly so this display is consistent for both
    // normal boards and saved positions using sparse scoring.
    const scores = {player1: 0, player2: 0};
    for (let row = 0; row < board.rows; row++) {
        for (let col = 0; col <= row; col++) {
            const value = board.at(row, col);
            if (value !== 0 && board.isSafe(row, col)) {
                if (value > 0) scores.player1 += value;
                else scores.player2 += Math.abs(value);
            }
        }
    }

    const nextValue = board.nextMove();
    const nextIndex = nextValue > 0 ? 0 : 1;
    const totalPieces = [Math.ceil((board.cellCount - 1) / 2), Math.floor((board.cellCount - 1) / 2)];
    const placedPieces = [Math.ceil(board.filled / 2), Math.floor(board.filled / 2)];
    const losses = [0, 0];
    if (gameOver) {
        for (let row = 0; row < board.rows; row++) {
            for (let col = 0; col <= row; col++) {
                const value = board.at(row, col);
                if (value !== 0 && !board.isSafe(row, col)) losses[value > 0 ? 0 : 1] += Math.abs(value);
            }
        }
    }
    const margin = Math.abs(scores.player1 - scores.player2);
    const result = scores.player1 === scores.player2 ? "It's a draw" : `${playerNames[scores.player1 > scores.player2 ? 0 : 1]} ${playerNames[scores.player1 > scores.player2 ? 0 : 1] === 'You' ? 'win' : 'wins'} by ${margin}`;

    return (
        <section className="black-hole-game" aria-label="Black Hole game">
            <div className="player-panels">
                {playerNames.map((name, index) => (
                    <div key={index} className={`player-panel player-panel-${index + 1}${!gameOver && nextIndex === index ? ' active-player' : ''}`}>
                        <span className="player-label">{index === 0 ? 'I · BLUE' : 'II · ORANGE'}</span>
                        <strong>{name}</strong>
                        <span className="safe-score">{gameOver ? 'Points kept' : 'Safe points'} <b>{Math.round((index === 0 ? scores.player1 : scores.player2) * (gameOver ? endingProgress : 1))}</b></span>
                        <div className="piece-tray" aria-label={`${placedPieces[index]} of ${totalPieces[index]} pieces placed`}>
                            {Array.from({length: totalPieces[index]}, (_, piece) => <span key={piece} className={piece < placedPieces[index] ? 'piece-used' : ''}>{piece + 1}</span>)}
                        </div>
                    </div>
                ))}
            </div>
            <div className="turn-banner" aria-live={gameOver ? 'off' : 'polite'} aria-atomic="true">
                {gameOver ? <><span className="turn-eyebrow">THE BLACK HOLE HAS OPENED</span><h2>{resultReady ? result : endingProgress > 0 ? 'Counting the points…' : 'Into the black hole…'}</h2><p aria-hidden={!resultReady}>{playerNames[0]} lost {Math.round(losses[0] * endingProgress)} · {playerNames[1]} lost {Math.round(losses[1] * endingProgress)}</p></> : <>
                    <span className={`next-piece next-piece-${nextIndex + 1}`} aria-hidden="true">{Math.abs(nextValue)}</span>
                    <div><span className="turn-eyebrow">{canPlay ? humanPlayer === undefined ? `${playerNames[nextIndex]}'s turn` : 'Your turn' : `${playerNames[nextIndex]} is thinking`}</span><h2>{canPlay ? `Place ${Math.abs(nextValue)}` : 'Finding a move…'}</h2></div>
                </>}
            </div>
            <span className="game-announcement" role="status" aria-live="polite" aria-atomic="true">{gameOver && resultReady ? `${result}. ${playerNames[0]} kept ${scores.player1} and lost ${losses[0]}. ${playerNames[1]} kept ${scores.player2} and lost ${losses[1]}.` : ''}</span>
            <BoardView board={board} onCellClick={handleCellClick} hints={hints} hintsPending={hintsPending} numericOverlay={numericOverlay} canPlay={canPlay} suggestedCell={suggestedCell}/>
            <p className="board-caption">{gameOver ? 'Dimmed pieces were swallowed. The remaining pieces are safe.' : 'One space stays empty. Its neighbors will be swallowed.'}</p>
            <details className="safe-explanation"><summary>What are safe points?</summary><p>A piece is safe once all its neighboring spaces are filled. It can no longer fall into the black hole. Keep more points than your opponent to win.</p></details>
            <div className="game-actions">
                {showUndoButton && <button type="button" onClick={handleUndo} disabled={(!canPlay && !gameOver) || board.filled === 0}>Undo move</button>}
                {gameOver && resultReady && <button type="button" className="rematch-button" onClick={handleRestart}>Play again</button>}
            </div>
        </section>
    );
};

export default BlackHoleGame;
